import React, { useEffect, useState } from 'react';
import { Star, Image as ImageIcon, X, Loader2, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';

const StarRow = ({ value, onChange, size = 5, readOnly = false }) => (
  <div className="flex gap-0.5" data-testid={readOnly ? 'review-stars-readonly' : 'review-stars-input'}>
    {[1, 2, 3, 4, 5].map(n => (
      <button
        key={n}
        type="button"
        disabled={readOnly}
        onClick={() => !readOnly && onChange && onChange(n)}
        className={readOnly ? 'cursor-default' : 'cursor-pointer'}
        data-testid={readOnly ? undefined : `review-star-${n}`}
      >
        <Star
          className={`w-${size} h-${size} ${n <= value ? 'fill-[#F46A25] text-[#F46A25]' : 'text-neutral-300'}`}
          strokeWidth={1.5}
        />
      </button>
    ))}
  </div>
);

const ReviewSection = ({ product }) => {
  const { user } = useAuth();
  const { settings } = useSettings();
  const showDate = settings?.show_reviews_date !== false;
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [photos, setPhotos] = useState([]); // list of data URLs
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const load = () => {
    if (!product?.id) return;
    setLoading(true);
    api.get(`/products/${product.id}/reviews`)
      .then(r => setReviews(r.data || []))
      .catch(() => setReviews([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [product?.id]);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (photos.length + files.length > 6) {
      toast.error('Up to 6 photos per review'); return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        const r = await api.post('/reviews/upload-photo', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push(r.data.url);
      }
      setPhotos(p => [...p, ...uploaded]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const submit = async () => {
    if (text.trim().length < 5) { toast.error('Review must be at least 5 characters'); return; }
    setSubmitting(true);
    try {
      await api.post(`/products/${product.id}/reviews`, {
        rating, title: title.trim() || null, text: text.trim(), photos,
      });
      setSubmitted(true);
      setShowForm(false);
      setTitle(''); setText(''); setPhotos([]); setRating(5);
      toast.success('Thanks — your review is awaiting moderation.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit failed');
    } finally { setSubmitting(false); }
  };

  return (
    <section className="mt-10" data-testid="review-section">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="text-xl md:text-2xl font-black text-[#12263A]">Customer reviews</h2>
          <div className="flex items-center gap-2 mt-1">
            <StarRow value={Math.round(product.rating || 0)} readOnly />
            <span className="text-sm text-neutral-700 font-semibold">{Number(product.rating || 0).toFixed(1)}</span>
            <span className="text-xs text-neutral-500">· {product.reviews || 0} approved review{product.reviews === 1 ? '' : 's'}</span>
          </div>
        </div>
        {user ? (
          <button
            onClick={() => setShowForm(v => !v)}
            data-testid="review-open-form-btn"
            className="bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold text-sm px-4 py-2 rounded-sm"
          >
            {showForm ? 'Cancel' : 'Write a review'}
          </button>
        ) : (
          <Link
            to={`/login?redirect=${encodeURIComponent(window.location.pathname)}`}
            data-testid="review-login-cta"
            className="text-[#F46A25] font-bold text-sm hover:underline"
          >Sign in to write a review →</Link>
        )}
      </div>

      {submitted && !showForm && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 mb-4 flex items-start gap-2 text-sm text-emerald-800" data-testid="review-pending-notice">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <div>Your review is in the moderation queue. It'll appear on this page once an admin approves it.</div>
        </div>
      )}

      {showForm && (
        <div className="bg-white border border-neutral-200 rounded-sm p-4 mb-6" data-testid="review-form">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-sm font-semibold text-[#12263A]">Your rating:</span>
            <StarRow value={rating} onChange={setRating} />
          </div>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Review title (optional)"
            data-testid="review-title-input"
            className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm mb-2 focus:outline-none focus:border-[#F46A25]"
            maxLength={140}
          />
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            rows={4}
            placeholder="What did you think of the product? Was the delivery quick? Would you recommend it to another tradesperson?"
            data-testid="review-text-input"
            className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]"
          />
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <label className="cursor-pointer text-xs font-semibold text-[#12263A] border border-neutral-300 hover:border-[#F46A25] px-3 py-1.5 rounded-sm flex items-center gap-1">
              <ImageIcon className="w-3.5 h-3.5" />
              {uploading ? 'Uploading…' : 'Add photos'}
              <input type="file" multiple accept="image/*" hidden onChange={handlePhotoUpload} data-testid="review-photo-input" />
            </label>
            {photos.map((p, i) => (
              <div key={i} className="relative w-14 h-14 border border-neutral-200 rounded-sm overflow-hidden" data-testid={`review-photo-thumb-${i}`}>
                <img src={p} alt={`upload ${i + 1}`} className="w-full h-full object-cover" />
                <button
                  onClick={() => setPhotos(ps => ps.filter((_, j) => j !== i))}
                  type="button"
                  className="absolute top-0 right-0 bg-black/60 text-white rounded-bl-sm p-0.5"
                  data-testid={`review-photo-remove-${i}`}
                ><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="review-submit-btn"
              className="bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold text-sm px-4 py-2 rounded-sm disabled:opacity-60 flex items-center gap-1"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit review
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="border border-neutral-300 text-[#12263A] font-bold text-sm px-4 py-2 rounded-sm"
              data-testid="review-cancel-btn"
            >Cancel</button>
          </div>
          <p className="text-[11px] text-neutral-500 mt-3">
            Reviews are moderated to keep them helpful. Yours will appear once approved.
          </p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[#F46A25] mx-auto" /></div>
      ) : reviews.length === 0 ? (
        <div className="text-sm text-neutral-500 border border-dashed border-neutral-200 rounded-sm p-6 text-center" data-testid="review-empty">
          No reviews yet — be the first to share your experience.
        </div>
      ) : (
        <ul className="space-y-3">
          {reviews.map(r => (
            <li key={r.id} className="bg-white border border-neutral-200 rounded-sm p-4" data-testid={`review-item-${r.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <StarRow value={r.rating} readOnly />
                <span className="font-bold text-[#12263A] text-sm">{r.reviewer_name}</span>
                {showDate && (
                  <span className="text-[11px] text-neutral-500 ml-auto" data-testid={`review-date-${r.id}`}>
                    {new Date(r.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                )}
              </div>
              {r.title && <div className="font-semibold text-[#12263A] text-sm mb-1">{r.title}</div>}
              <p className="text-sm text-neutral-700 whitespace-pre-line">{r.text}</p>
              {r.photos && r.photos.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {r.photos.map((p, i) => (
                    <a key={i} href={p} target="_blank" rel="noreferrer" className="w-16 h-16 border border-neutral-200 rounded-sm overflow-hidden">
                      <img src={p} alt={`Review photo ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default ReviewSection;
